import { proxy } from 'valtio';

import { getApiDomain } from '@cartwhl/common/api/api-utils';
import * as authStore from '@cartwhl/common/stores/authStore';

const WEBSOCKET_CONNECT_TIMEOUT_MS = 10_000;
const WEBSOCKET_PING_INTERVAL_MS = 30_000;

type WebSocketStatus = 'initial' | 'connecting' | 'connected' | 'error';

type WebSocketWithConnectionInfo = WebSocket & {
  connectionID?: string;
  endpoint?: string;
};

type WebSocketConnectionInfo = {
  connectionID: string;
  endpoint: string;
};

type WebSocketPingData = {
  state?: string;
  connectionID?: string;
  endpoint?: string;
};

export type AppletWebSocketEventMap = {
  TIMELINE_UPDATED: {
    state: 'TIMELINE_UPDATED';
    jobID: string;
    sceneID?: string;
    projectID?: string;
  };
};

type AppletWebSocketData =
  | WebSocketPingData
  | AppletWebSocketEventMap[keyof AppletWebSocketEventMap];

type EventHandler<T extends keyof AppletWebSocketEventMap> = (
  data: AppletWebSocketEventMap[T],
) => void;

export const state = proxy<{
  status: WebSocketStatus;
  connectionID: string | null;
  endpoint: string | null;
  error: string | null;
}>({
  status: 'initial',
  connectionID: null,
  endpoint: null,
  error: null,
});

const subscribers: {
  [K in keyof AppletWebSocketEventMap]: Set<EventHandler<K>>;
} = {
  TIMELINE_UPDATED: new Set(),
};

let webSocket: WebSocketWithConnectionInfo | null = null;
let connectPromise: Promise<WebSocketConnectionInfo> | null = null;
let pingInterval: number | undefined;

export function on<T extends keyof AppletWebSocketEventMap>(
  event: T,
  handler: EventHandler<T>,
) {
  subscribers[event].add(handler);

  return () => {
    subscribers[event].delete(handler);
  };
}

export async function connect() {
  await getConnectionInfo();
}

export function getConnectionInfo(): Promise<WebSocketConnectionInfo> {
  if (
    webSocket?.readyState === WebSocket.OPEN &&
    webSocket.connectionID &&
    webSocket.endpoint
  ) {
    return Promise.resolve({
      connectionID: webSocket.connectionID,
      endpoint: webSocket.endpoint,
    });
  }

  if (connectPromise) {
    return connectPromise;
  }

  webSocket?.close();
  state.status = 'connecting';
  state.error = null;

  connectPromise = new Promise<WebSocketConnectionInfo>((resolve, reject) => {
    const ws = new WebSocket(
      getApiDomain('websocket'),
    ) as WebSocketWithConnectionInfo;
    webSocket = ws;
    let settled = false;

    const settle = (
      result:
        | { status: 'ready'; connectionInfo: WebSocketConnectionInfo }
        | { status: 'error'; error: Error },
    ) => {
      if (settled) {
        return;
      }

      settled = true;
      connectPromise = null;
      window.clearTimeout(timeoutId);

      if (result.status === 'ready') {
        resolve(result.connectionInfo);
        return;
      }

      state.status = 'error';
      state.error = result.error.message;
      reject(result.error);
    };

    const timeoutId = window.setTimeout(() => {
      ws.close();
      settle({
        status: 'error',
        error: new Error('Timed out connecting to applet update service.'),
      });
    }, WEBSOCKET_CONNECT_TIMEOUT_MS);

    ws.onopen = () => {
      ws.send(
        JSON.stringify({ action: 'ping', user: authStore.state.userID ?? '' }),
      );
      startPingInterval(ws);
    };

    ws.onmessage = (event) => {
      const data = parseWebSocketMessage(event.data);
      if (!data) {
        return;
      }

      if (data.state === undefined && data.connectionID && data.endpoint) {
        ws.connectionID = data.connectionID;
        ws.endpoint = data.endpoint;
        state.status = 'connected';
        state.connectionID = data.connectionID;
        state.endpoint = data.endpoint;
        state.error = null;
        settle({
          status: 'ready',
          connectionInfo: {
            connectionID: data.connectionID,
            endpoint: data.endpoint,
          },
        });
        return;
      }

      dispatchWebSocketEvent(data);
    };

    ws.onerror = () => {
      settle({
        status: 'error',
        error: new Error('Failed to connect to applet update service.'),
      });
    };

    ws.onclose = () => {
      const wasSettled = settled;
      stopPingInterval();
      if (webSocket === ws) {
        webSocket = null;
        state.connectionID = null;
        state.endpoint = null;
        if (state.status === 'connected') {
          state.status = 'initial';
        }
      }

      if (wasSettled) {
        return;
      }

      settle({
        status: 'error',
        error: new Error(
          'Applet update service disconnected before connection details were available.',
        ),
      });
    };
  });

  return connectPromise;
}

export function disconnect() {
  webSocket?.close();
  webSocket = null;
  connectPromise = null;
  stopPingInterval();
  state.status = 'initial';
  state.connectionID = null;
  state.endpoint = null;
  state.error = null;
}

function dispatchWebSocketEvent(data: AppletWebSocketData) {
  if (isTimelineUpdatedEvent(data)) {
    subscribers.TIMELINE_UPDATED.forEach((handler) => handler(data));
  }
}

function startPingInterval(ws: WebSocket) {
  stopPingInterval();
  pingInterval = window.setInterval(() => {
    ws.send('ping');
  }, WEBSOCKET_PING_INTERVAL_MS);
}

function stopPingInterval() {
  if (pingInterval === undefined) {
    return;
  }

  window.clearInterval(pingInterval);
  pingInterval = undefined;
}

function parseWebSocketMessage(data: unknown): AppletWebSocketData | null {
  if (typeof data !== 'string') {
    return null;
  }

  try {
    return JSON.parse(data) as AppletWebSocketData;
  } catch {
    return null;
  }
}

function isTimelineUpdatedEvent(
  data: AppletWebSocketData,
): data is AppletWebSocketEventMap['TIMELINE_UPDATED'] {
  return (
    data.state === 'TIMELINE_UPDATED' &&
    (('sceneID' in data && typeof data.sceneID === 'string') ||
      ('projectID' in data && typeof data.projectID === 'string'))
  );
}
